import { Controller } from "@hotwired/stimulus"

// 職歴書なし版AI面談の「職歴入力フォーム」（音声面談の前段）。
// resume_wizard_controller の職歴収集部分を流用し、氏名・自己PR・生成は持たない。
// 各回答は PATCH save で intake_data に自動保存し、リロード時は current_step から再開する。
// 入力完了後は POST complete で面談画面へ遷移する。
//
// 保存プロトコル（resume と同型）:
//   ジョブ新規追加: { current_step, add_job: true, job_patch: {company: ...} }
//   ジョブ更新:     { current_step, job_index, job_patch: {...} }
//   トップレベル:   { current_step, patch: {qualifications: ...} }
export default class extends Controller {
  static targets = [
    "initialData", "messages",
    "textBlock", "textInput", "textSkip",
    "areaBlock", "areaInput", "areaSkip",
    "periodBlock", "startYear", "startMonth", "endYear", "endMonth", "currentCheck", "currentLabel", "endSelects",
    "choiceBlock", "moreBlock", "doneBlock", "doneCount"
  ]
  static values = { saveUrl: String, completeUrl: String, sessionUrl: String, initialStep: String }

  static STEPS = {
    job_company:      { type: "text" },
    job_period:       { type: "period" },
    job_employment:   { type: "choice" },
    job_business:     { type: "text", skippable: true },
    job_duties:       { type: "area", rows: 5 },
    job_achievements: { type: "area", rows: 3, skippable: true },
    job_more:         { type: "more" },
    qualifications:   { type: "area", rows: 3, skippable: true },
    done:             { type: "done" }
  }

  static PLACEHOLDERS = {
    job_company: "例：株式会社サンプル",
    job_business: "例：法人向けSaaSの開発・販売",
    job_duties: "例）\n・法人顧客への新規開拓営業\n・既存顧客のフォローと契約更新\n・若手メンバーの育成",
    job_achievements: "例：新規顧客を年間30社開拓、部門売上 前年比120%達成",
    qualifications: "例）\n普通自動車第一種運転免許（2018年3月）\nTOEIC 800点（2022年5月）"
  }

  connect() {
    this.intake = JSON.parse(this.initialDataTarget.textContent || "{}")
    this.intake.jobs ||= []
    this.step = this.initialStepValue || "job_company"

    if (this.step === "job_company" && this.intake.jobs.length === 0) {
      this.say("面談の前に、これまでの職歴を順番にお伺いします。\nわかる範囲で入力してください 📝")
    } else {
      this.say("おかえりなさい。前回の続きから再開します。")
    }
    const prompt = this.promptFor(this.step)
    if (prompt) this.say(prompt)
    this.showInput(this.step)
  }

  // --- ステップごとの質問文 ---

  promptFor(step) {
    const n = this.intake.jobs.length + 1
    const co = this.currentJob()?.company || "その会社"
    switch (step) {
      case "job_company":
        return n === 1
          ? "直近（現在または最後）に在籍した会社名を教えてください。\n「株式会社」などの法人格も含めた正式名称でお願いします。"
          : `次に、その前に在籍した会社名を教えてください。（${n}社目）\n法人格を含めた正式名称でお願いします。`
      case "job_period": return `「${co}」での在籍期間を選んでください。`
      case "job_employment": return "雇用形態を選んでください。"
      case "job_business": return `「${co}」の事業内容を一言で教えてください。（任意・スキップ可）`
      case "job_duties": return `「${co}」での業務内容を教えてください。\n箇条書き・ざっくりで大丈夫です ✍️`
      case "job_achievements": return "主な実績・成果があれば教えてください。（任意・スキップ可）\n数字を入れると効果的です。"
      case "job_more": return `「${co}」の入力が完了しました。さらに前の職歴を追加しますか？`
      case "qualifications": return "保有資格があれば1行ずつ入力してください。（任意・スキップ可）\n例：日商簿記2級（2020年6月）"
      case "done": return "ありがとうございました。これで職歴の入力は完了です。\n準備ができたら、AIアドバイザー「田中」との音声面談に進みましょう 🎤"
      default: return ""
    }
  }

  currentJob() {
    if (!this.intake.jobs || this.intake.jobs.length === 0) return null
    return this.intake.jobs[this.intake.jobs.length - 1]
  }

  // --- 入力ハンドラ ---

  submitText(event) {
    if (event.type === "keydown" && event.isComposing) return // IME確定のEnterは無視
    event.preventDefault?.()
    const value = this.textInputTarget.value.trim()
    if (!value) return
    this.userSay(value)
    this.textInputTarget.value = ""

    switch (this.step) {
      case "job_company":
        this.intake.jobs.push({ company: value })
        this.goTo("job_period", { add_job: true, job_patch: { company: value } })
        break
      case "job_business": {
        const job = this.currentJob()
        if (!job) return
        job.business = value
        this.goTo("job_duties", this.jobPatch({ business: value }))
        break
      }
    }
  }

  submitArea() {
    const value = this.areaInputTarget.value.trim()
    if (!value) return
    this.userSay(value.length > 60 ? value.slice(0, 60) + "…" : value)
    this.areaInputTarget.value = ""

    switch (this.step) {
      case "job_duties": {
        const job = this.currentJob()
        if (!job) return
        job.duties = value
        this.goTo("job_achievements", this.jobPatch({ duties: value }))
        break
      }
      case "job_achievements": {
        const job = this.currentJob()
        if (!job) return
        job.achievements = value
        this.goTo("job_more", this.jobPatch({ achievements: value }))
        break
      }
      case "qualifications":
        this.intake.qualifications = value
        this.goTo("done", { patch: { qualifications: value } })
        break
    }
  }

  // 任意項目のスキップ
  skip() {
    this.userSay("スキップ")
    switch (this.step) {
      case "job_business":
        if (!this.currentJob()) return
        this.goTo("job_duties", this.jobPatch({ business: "" }))
        break
      case "job_achievements":
        if (!this.currentJob()) return
        this.goTo("job_more", this.jobPatch({ achievements: "" }))
        break
      case "qualifications":
        this.goTo("done", { patch: { qualifications: "" } })
        break
    }
  }

  toggleCurrent() {
    const current = this.currentCheckTarget.checked
    this.currentLabelTarget.classList.toggle("hidden", !current)
    this.endSelectsTarget.classList.toggle("hidden", current)
  }

  submitPeriod() {
    const job = this.currentJob()
    if (!job) return

    const pad = (m) => String(m).padStart(2, "0")
    const current = this.currentCheckTarget.checked
    const period = {
      start: `${this.startYearTarget.value}-${pad(this.startMonthTarget.value)}`,
      end: current ? "" : `${this.endYearTarget.value}-${pad(this.endMonthTarget.value)}`,
      current: current
    }
    const startLabel = `${this.startYearTarget.value}年${Number(this.startMonthTarget.value)}月`
    const endLabel = current ? "現在" : `${this.endYearTarget.value}年${Number(this.endMonthTarget.value)}月`
    this.userSay(`${startLabel} ～ ${endLabel}`)

    job.period = period
    this.goTo("job_employment", this.jobPatch({ period: period }))
  }

  submitEmployment(event) {
    const job = this.currentJob()
    if (!job) return

    const value = event.currentTarget.dataset.value
    this.userSay(value)
    job.employment = value
    this.goTo("job_business", this.jobPatch({ employment: value }))
  }

  addJob() {
    this.userSay("もう1社追加する")
    this.goTo("job_company", {})
  }

  continueNext() {
    this.userSay("職歴の入力を終えて次へ進む")
    this.goTo("qualifications", {})
  }

  async complete() {
    try {
      const response = await fetch(this.completeUrlValue, {
        method: "POST",
        headers: { "Accept": "text/html", "X-CSRF-Token": this.csrfToken() }
      })
      if (!response.ok && response.status >= 400) throw new Error()
      window.location.href = this.sessionUrlValue
    } catch {
      this.say("面談画面への遷移に失敗しました。少し時間をおいて、もう一度お試しください。")
    }
  }

  // --- 進行・保存 ---

  jobPatch(patch) {
    return { job_index: this.intake.jobs.length - 1, job_patch: patch }
  }

  goTo(step, savePayload) {
    this.step = step
    this.save({ current_step: step, ...savePayload })
    const prompt = this.promptFor(step)
    if (prompt) this.say(prompt)
    this.showInput(step)
  }

  async save(payload) {
    try {
      await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify(payload)
      })
    } catch {
      // 自動保存の失敗は会話を止めない（次の保存でcurrent_stepごと回復する）
    }
  }

  // --- UI ---

  showInput(step) {
    const config = this.constructor.STEPS[step]
    if (!config) return

    const blocks = {
      text: this.textBlockTarget, area: this.areaBlockTarget, period: this.periodBlockTarget,
      choice: this.choiceBlockTarget, more: this.moreBlockTarget, done: this.doneBlockTarget
    }
    Object.values(blocks).forEach((el) => { el.classList.add("hidden"); el.classList.remove("flex") })

    const block = blocks[config.type]
    block.classList.remove("hidden")
    if ([ "choice", "more", "done" ].includes(config.type)) block.classList.add("flex")

    if (config.type === "text") {
      this.textInputTarget.placeholder = this.constructor.PLACEHOLDERS[step] || ""
      this.textSkipTarget.classList.toggle("hidden", !config.skippable)
      this.textInputTarget.focus()
    } else if (config.type === "area") {
      this.areaInputTarget.placeholder = this.constructor.PLACEHOLDERS[step] || ""
      this.areaInputTarget.rows = config.rows || 4
      this.areaSkipTarget.classList.toggle("hidden", !config.skippable)
      this.areaInputTarget.focus()
    } else if (config.type === "period") {
      // 1社目は「在籍中」を初期ON（直近の会社から入力するため）
      this.currentCheckTarget.checked = this.intake.jobs.length === 1
      this.toggleCurrent()
    } else if (config.type === "done") {
      this.doneCountTarget.textContent = this.intake.jobs.length
    }
  }

  say(text) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex justify-start"
    wrapper.innerHTML = `
      <div class="mr-2 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100">
        <span class="text-sm">✨</span>
      </div>
      <div class="max-w-[78%] whitespace-pre-wrap rounded-2xl rounded-tl-[4px] border border-gray-200 bg-white px-4 py-2.5 text-sm leading-relaxed text-gray-800"></div>`
    wrapper.lastElementChild.textContent = text
    this.messagesTarget.appendChild(wrapper)
    this.scrollToBottom()
  }

  userSay(text) {
    const wrapper = document.createElement("div")
    wrapper.className = "flex justify-end"
    wrapper.innerHTML = `
      <div class="max-w-[78%] whitespace-pre-wrap rounded-2xl rounded-tr-[4px] bg-indigo-600 px-4 py-2.5 text-sm leading-relaxed text-white"></div>`
    wrapper.firstElementChild.textContent = text
    this.messagesTarget.appendChild(wrapper)
    this.scrollToBottom()
  }

  scrollToBottom() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
